import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import ReactToolTip from 'react-tooltip';
import { findMyActiveSearch } from 'react/matchmaking/unified/header/myActiveSearch';
import { searchOptionsForMode } from 'react/matchmaking/findMatch/findMatchModes';
import { lfgButtonLabel, lfgModeFor, lfgPostRequest } from 'react/chat/lfg/lfgPostRequest';
import { __ } from 'i18n/index';

const SEARCH_CHECK_INTERVAL_MS = 1000;

function useHasOpenSearch() {
	const [hasOpenSearch, setHasOpenSearch] = useState(() => !!findMyActiveSearch());
	const recheck = () => setHasOpenSearch(!!findMyActiveSearch());
	useEffect(() => {
		const interval = setInterval(recheck, SEARCH_CHECK_INTERVAL_MS);
		return () => clearInterval(interval);
	}, []);
	return [hasOpenSearch, recheck];
}

function searchesGame(preferredGamesList, ladderId) {
	if(!preferredGamesList) {
		return false;
	}
	for(const game of preferredGamesList.values()) {
		if(game.gameRoom && game.gameRoom.id === ladderId) {
			return true;
		}
	}
	return false;
}

function searchModeName(options) {
	return options.teamSize === 2 ? __('doubles') : __('friendlies');
}

// Says which search the button starts: the mode and its set length.
function describeSearch(options) {
	const modeName = searchModeName(options);
	if(!options.gameCount) {
		return __('Start an endless %s search and post it here for anyone to accept.', modeName);
	}
	return __('Start a %s best-of-%d search and post it here for anyone to accept.', modeName, options.gameCount);
}

// The LFG button in a game room's composer ("+ Friendlies" or "+ Doubles"):
// posts an LFG card and starts its search
// with the team size and set length of the mode last started on the Play page. LFGs are always
// unranked, since ranked is only found through automatic matching. The composer text, if
// any, becomes the card's title (takeTitle returns it and clears the composer).
function LfgComposerButton({ chatMessages, takeTitle, preferredGamesList, currentMatch, modeSettings, lastStartedMode }) {
	const [hasOpenSearch, recheckOpenSearch] = useHasOpenSearch();
	const [posting, setPosting] = useState(false);
	const chatRoom = chatMessages.chatRoom;
	const ladder = chatRoom ? chatRoom.ladder : null;

	if(!ladder || !searchesGame(preferredGamesList, ladder.id) || currentMatch || hasOpenSearch || posting) {
		return null;
	}

	const options = searchOptionsForMode(lfgModeFor(lastStartedMode), modeSettings, null);
	const postLfg = (event) => {
		event.preventDefault();
		const card = chatMessages.postLfg(lfgPostRequest(lastStartedMode, modeSettings, chatMessages.chatRoomId, takeTitle ? takeTitle() : ''));
		setPosting(true);
		// The reply carries the new search, so check for it before showing the button again.
		Promise.resolve(card && card.sendPromise).finally(() => {
			recheckOpenSearch();
			setPosting(false);
		});
	};

	const tooltipId = `chat-composer-lfg-${chatMessages.chatRoomId}`;
	return (
		<>
			<button
				type='button'
				className='chat-composer-lfg'
				data-tip={describeSearch(options)}
				data-for={tooltipId}
				onClick={postLfg}
			>
				{lfgButtonLabel(lastStartedMode)}
			</button>
			<ReactToolTip id={tooltipId} type='dark' effect='solid' place='top' />
		</>
	);
}

export default connect((state) => ({
	preferredGamesList: state.matchmaking.preferredGamesList,
	currentMatch: state.matchmaking.currentMatch,
	modeSettings: state.findMatch.modeSettings,
	lastStartedMode: state.findMatch.lastStartedMode,
}))(LfgComposerButton);
